import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Calendar, BarChart3, Activity, FileText, GitCompare,
  Smartphone, ArrowRight, CheckCircle, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import DemoModal from "@/components/DemoModal";
import AuthModal from "@/components/AuthModal";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_performance-hub-263/artifacts/7qu8f7d9_next%20tackle.png";
const MIC_IMAGE = "https://customer-assets.emergentagent.com/job_performance-hub-263/artifacts/dxjtboem_MIC.jpeg";

/* ─── Hero ──────────────────────────────────────────────────────────────── */
function HeroSection({ onDemoClick, onTrialClick }) {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
      />
      {/* Soft documentary overlay — not heavy */}
      <div className="absolute inset-0 bg-ink/50" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-40 w-full">
        <div className="max-w-2xl">
          <p
            data-testid="hero-overline"
            className="text-xs tracking-[0.2em] uppercase font-semibold text-white/60 mb-6"
          >
            Football Analytics Platform
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold text-white leading-[1.08] mb-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Better Player Development,<br />Built on Data.
          </h1>
          <p className="text-base md:text-lg text-white/70 leading-relaxed mb-10 max-w-lg">
            A simple platform for football academies to track, analyze, and improve player performance.
          </p>
          <div className="flex flex-col sm:flex-row items-start gap-3">
            <Button
              onClick={onTrialClick}
              data-testid="hero-start-trial-btn"
              className="bg-forest text-cream rounded-full px-8 py-4 h-auto font-medium hover:bg-forest/90 transition-colors"
            >
              Start Free Trial
            </Button>
            <Button
              onClick={onDemoClick}
              data-testid="hero-request-demo-btn"
              variant="outline"
              className="border border-white/30 text-white bg-transparent hover:bg-white/10 rounded-full px-8 py-4 h-auto font-medium transition-colors"
            >
              Request Demo
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Trust bar ─────────────────────────────────────────────────────────── */
function TrustBar() {
  const clubs = [
    "Minerva FC", "International School of Stuttgart", "P.S. Ethnikos", "BrothersFC", "Velocity FC",
  ];
  return (
    <section className="py-12 bg-white border-b border-ink/5" data-testid="trust-bar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-ink/40 mb-6 text-center">
          Used by 5 football academies
        </p>
        <div className="flex flex-wrap justify-center gap-x-10 gap-y-3">
          {clubs.map((c) => (
            <span key={c} className="text-sm font-medium text-ink/40">{c}</span>
          ))}
        </div>
        <p className="text-center text-xs text-ink/30 mt-6 italic">
          Built from real academy environments, not theory.
        </p>
      </div>
    </section>
  );
}

/* ─── Features ──────────────────────────────────────────────────────────── */
function FeaturesSection() {
  const features = [
    { icon: Calendar, title: "Attendance Tracking", desc: "Log and monitor session attendance automatically." },
    { icon: Activity, title: "Training Performance", desc: "Record intensity, drills, and session notes per player." },
    { icon: BarChart3, title: "Player Progress", desc: "Visualize development trends over weeks and seasons." },
    { icon: GitCompare, title: "Match & Drill Analytics", desc: "Break down performance by drill type and match role." },
    { icon: FileText, title: "Coach & Parent Reports", desc: "Auto-generate clear progress reports for all stakeholders." },
    { icon: GitCompare, title: "Team Comparison", desc: "Benchmark players against squad averages objectively." },
  ];

  return (
    <section className="py-24 bg-cream" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="mb-14">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-forest mb-3">
            What It Does
          </p>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-ink max-w-xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Every tool a coaching team actually needs.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/8 border border-ink/8 rounded-2xl overflow-hidden">
          {features.map(({ icon: Icon, title, desc }, i) => (
            <div
              key={title}
              className="bg-white p-8 hover:bg-cream transition-colors"
              data-testid={`feature-card-${i + 1}`}
            >
              <Icon className="w-5 h-5 text-forest mb-5" strokeWidth={1.5} />
              <h3
                className="text-base font-semibold text-ink mb-2"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {title}
              </h3>
              <p className="text-sm text-ink/60 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link
            to="/features"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-forest hover:text-forest/80 transition-colors"
            data-testid="features-see-all-link"
          >
            See all features <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─── How It Works ──────────────────────────────────────────────────────── */
function HowItWorksSection() {
  const steps = [
    { num: "01", title: "Record Sessions", desc: "Log training sessions with attendance, drill type, and intensity in under a minute." },
    { num: "02", title: "Track Player Activity", desc: "Build a complete training history for each player automatically." },
    { num: "03", title: "Analyze Performance", desc: "View dashboards showing individual and team progress over time." },
    { num: "04", title: "Improve Decisions", desc: "Use data to guide squad selection, session planning, and player conversations." },
  ];
  return (
    <section className="py-24 bg-white" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="mb-16">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-forest mb-3">
            How It Works
          </p>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-ink max-w-md"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            From first session to clear insight.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, i) => (
            <div key={step.num} className="group" data-testid={`step-${i + 1}`}>
              <div
                className="text-6xl font-semibold text-forest/10 tracking-tighter mb-4 leading-none select-none"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {step.num}
              </div>
              <h3
                className="text-base font-semibold text-ink mb-2"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {step.title}
              </h3>
              <p className="text-sm text-ink/55 leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Benefits ──────────────────────────────────────────────────────────── */
function BenefitsSection() {
  const benefits = [
    "Save time on manual tracking and reporting",
    "Understand every player's progress clearly",
    "Make objective, data-informed coaching decisions",
    "Communicate player development to parents with confidence",
  ];
  return (
    <section className="py-24 bg-forest" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-white/50 mb-4">
              Why It Matters
            </p>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white leading-tight"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Coaching is clearer when the data does the work.
            </h2>
          </div>
          <ul className="space-y-5">
            {benefits.map((b, i) => (
              <li
                key={i}
                className="flex items-start gap-4"
                data-testid={`benefit-${i + 1}`}
              >
                <CheckCircle className="w-5 h-5 text-white/50 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <span className="text-base text-white/80 leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ─── Credibility ───────────────────────────────────────────────────────── */
function CredibilitySection() {
  return (
    <section className="bg-cream overflow-hidden" data-testid="credibility-section">
      <div className="grid lg:grid-cols-2 min-h-[560px]">
        {/* Image — documentary, minimal overlay */}
        <div className="relative min-h-[320px] lg:min-h-0">
          <img
            src={MIC_IMAGE}
            alt="Minerva FC coaching staff at MIC Cup"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-ink/20" />
          <div className="absolute bottom-6 left-6">
            <span className="inline-block bg-white/90 text-ink text-xs font-semibold px-3 py-1.5 rounded-full tracking-wide">
              MIC Cup · Costa Brava, Spain
            </span>
          </div>
        </div>

        {/* Copy */}
        <div className="px-8 py-16 lg:px-16 lg:py-20 flex flex-col justify-center bg-white">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-forest mb-5">
            Our Background
          </p>
          <h2
            className="text-2xl sm:text-3xl tracking-tight font-medium text-ink mb-6 leading-snug"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Built from the Ground,<br />Not Just Code.
          </h2>
          <p className="text-base text-ink/60 leading-relaxed mb-5">
            Before building NextTackle, our founders worked directly with <strong className="text-ink font-medium">Minerva Football Club</strong> during the <strong className="text-ink font-medium">MIC Cup</strong> — one of the world's most recognized youth football tournaments, held in Costa Brava, Spain.
          </p>
          <p className="text-base text-ink/60 leading-relaxed mb-8">
            That experience — observing real training sessions, understanding how coaches manage player data, and seeing what actually gets used on the pitch — shaped every decision in how this platform was built.
          </p>
          <p className="text-sm text-ink/40 italic">
            "We didn't design this from assumptions. We built it from what we saw coaches actually need."
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── Testimonials ──────────────────────────────────────────────────────── */
function TestimonialsSection() {
  const testimonials = [
    {
      quote: "Helps us track player consistency without extra effort.",
      name: "Chirstos",
      role: "Head of Sports",
      academy: "International School of Stuttgart",
    },
    {
      quote: "Simple to use, and useful for communicating with parents.",
      name: "Konstantinos Nikolaidis",
      role: "Lead Coach",
      academy: "P.S. Ethnikos",
    },
    {
      quote: "Built by people who understand football, not just software.",
      name: "Shahebaz Desai",
      role: "Director",
      academy: "BrothersFC",
    },
  ];
  return (
    <section className="py-24 bg-cream" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="mb-14">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-forest mb-3">
            From Coaches
          </p>
          <h2
            className="text-2xl sm:text-3xl tracking-tight font-medium text-ink"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            What people are saying.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={t.name}
              className="bg-white rounded-2xl p-8 border border-ink/5"
              data-testid={`testimonial-${i + 1}`}
            >
              <p
                className="text-base text-ink/75 leading-relaxed mb-8 italic"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                "{t.quote}"
              </p>
              <div className="pt-6 border-t border-ink/5">
                <div className="text-sm font-semibold text-ink">{t.name}</div>
                <div className="text-xs text-ink/40 mt-0.5">{t.role}, {t.academy}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Final CTA ─────────────────────────────────────────────────────────── */
function FinalCTASection({ onDemoClick, onTrialClick }) {
  return (
    <section className="py-24 bg-white border-t border-ink/5" data-testid="final-cta-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-forest mb-5">
          Get Started
        </p>
        <h2
          className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-ink mb-6 max-w-xl mx-auto"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          See How It Works for Your Academy.
        </h2>
        <p className="text-base text-ink/55 mb-10 max-w-md mx-auto leading-relaxed">
          Free for 3 months. No credit card required. Set up in under 30 minutes.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            onClick={onTrialClick}
            data-testid="final-cta-trial-btn"
            className="bg-forest text-cream rounded-full px-8 py-4 h-auto font-medium hover:bg-forest/90 transition-colors"
          >
            Start Free Trial
          </Button>
          <Button
            onClick={onDemoClick}
            data-testid="final-cta-demo-btn"
            variant="outline"
            className="border border-ink/20 text-ink rounded-full px-8 py-4 h-auto font-medium hover:bg-ink/5 transition-colors bg-transparent"
          >
            Request Demo
          </Button>
        </div>
      </div>
    </section>
  );
}

/* ─── Page ──────────────────────────────────────────────────────────────── */
export default function HomePage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  return (
    <>
      <HeroSection onDemoClick={() => setDemoOpen(true)} onTrialClick={() => setAuthOpen(true)} />
      <TrustBar />
      <FeaturesSection />
      <HowItWorksSection />
      <BenefitsSection />
      <CredibilitySection />
      <TestimonialsSection />
      <FinalCTASection onDemoClick={() => setDemoOpen(true)} onTrialClick={() => setAuthOpen(true)} />
      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultTab="register" />
    </>
  );
}
